/*
  HCS UI-8 desktop nav (27 Jul 2026, Finbar)

  Every collection stays in the bar, on ONE row, at every desktop width. No
  overflow menu — Finbar ruled that out — so the bar shrinks to fit instead.

  How it fits:
   · a label may wrap onto a second line inside its own item ("GROW / TENTS")
     and never breaks mid-word, so the row narrows a long way on its own.
   · if it still runs past the end of the bar, --hcs-nav-scale is reduced until
     it fits. Type, padding and the right-hand gap are all multiples of that
     scale, so the row width is near enough proportional to it and the correction
     lands in one or two passes.

  The overhang is measured from the layout rather than predicted. A predicted
  minimum, built from each label's widest word, came out about 160px under the
  truth across eleven items, so the scale never engaged.

  Also handles:
   · flipping a dropdown that would run off the right edge
   · tap-to-open on touch screens, where :hover never fires
   · re-measuring once Montserrat has loaded, since metrics move
*/
(function () {
  'use strict';

  /* how far the bar may shrink before we stop; below this it stops being
     legible and the drawer takes over at 990px anyway */
  var MIN_SCALE = 0.74;

  function init(nav) {
    if (nav.__hcsNavBound) return;
    nav.__hcsNavBound = true;

    var inner = nav.querySelector('.hcs-nav__inner');
    var primary = nav.querySelector('.hcs-nav__primary');
    var secondary = nav.querySelector('.hcs-nav__secondary');
    if (!inner || !primary) return;

    var items = Array.prototype.slice.call(primary.querySelectorAll('.hcs-nav__item'));
    var secItems = secondary
      ? Array.prototype.slice.call(secondary.querySelectorAll('.hcs-nav__item'))
      : [];
    if (!items.length) return;





    /* ---- right-edge flip ------------------------------------------------ */

    /* read every position first, then apply the classes — never interleaved */
    function flip() {
      var bound = nav.getBoundingClientRect();
      var wanted = [];
      var i;
      for (i = 0; i < items.length; i++) {
        var panel = items[i].querySelector('.hcs-nav__panel');
        if (!panel) continue;
        var left = items[i].getBoundingClientRect().left;
        wanted.push([items[i], left + (panel.offsetWidth || 260) > bound.right]);
      }
      for (i = 0; i < wanted.length; i++) {
        wanted[i][0].classList.toggle('hcs-nav__item--flip', wanted[i][1]);
      }
    }

    /* ---- fit ------------------------------------------------------------ */

    var pending = null;
    var lastWidth = -1;
    var busy = false;

    function layout() {
      if (busy) return;
      if (!nav.offsetParent) return; /* below 990px the drawer has it */
      busy = true;
      try {
        fitPass();
      } finally {
        busy = false;
      }
    }

    /* Rightmost edge of anything actually visible in the row. Not simply the
       last element: in the collapsed (scrolled) bar the collections and the
       right-hand group are display:none and only SHOP is showing, and a hidden
       element reports a zero rect at x=0, which would read as "fits" whatever
       the truth. */
    function rowEnd() {
      var candidates = items.concat(secondary ? [secondary] : []);
      var right = 0;
      for (var i = 0; i < candidates.length; i++) {
        var r = candidates[i].getBoundingClientRect();
        if (!r.width && !r.height) continue; /* hidden */
        if (r.right > right) right = r.right;
      }
      return right;
    }

    /* > 0 means the row is running past the end of the bar */
    function overhang() {
      return Math.ceil(rowEnd() - inner.getBoundingClientRect().right);
    }

    function applyScale(v) {
      nav.style.setProperty('--hcs-nav-scale', String(Math.round(v * 1000) / 1000));
    }

    function fitPass() {
      var avail = inner.clientWidth;
      if (!avail) return;

      var scale = parseFloat(window.getComputedStyle(nav).getPropertyValue('--hcs-nav-scale')) || 1;
      var pass;

      /* Shrink until the row fits. This measures the real overhang rather than
         predicting the row's minimum — a predicted floor built from each label's
         widest word came out ~160px under the truth across eleven items, so the
         scale never engaged and the row overhung the bar by up to 72px. Widths
         are near enough proportional to the scale that this lands in one or two
         passes; the cap is only there so a pathological case cannot spin. */
      for (pass = 0; pass < 6; pass++) {
        var over = overhang();
        if (over <= 0) break;
        var next = Math.max(MIN_SCALE, (scale * avail) / (avail + over));
        if (next >= scale - 0.002) break; /* already as small as it may go */
        scale = next;
        applyScale(scale);
      }

      /* Grow back when room returns, stepping up only while it still fits. */
      for (pass = 0; pass < 5 && scale < 1; pass++) {
        var trial = Math.min(1, scale * 1.04);
        applyScale(trial);
        if (overhang() > 0) {
          applyScale(scale);
          break;
        }
        scale = trial;
      }

      flip();

      nav.setAttribute('data-hcs-avail', String(Math.round(avail)));
      nav.setAttribute('data-hcs-overhang', String(overhang()));
      nav.setAttribute('data-hcs-scale', String(Math.round(scale * 1000) / 1000));
    }

    function schedule(force) {
      if (pending) return;

      var run = function () {
        pending = null;
        if (busy) return;
        var w = Math.round(nav.clientWidth);
        if (!w) return;
        if (!force && w === lastWidth) return;
        lastWidth = w;
        layout();
      };

      /* requestAnimationFrame never fires in a background tab, and a page
         opened in one would otherwise sit with an unfitted nav until the
         window happened to be resized */
      if (document.hidden) {
        pending = window.setTimeout(run, 0);
      } else {
        pending = window.requestAnimationFrame(run);
      }
    }

    function relayout() {
      schedule(true);
    }

    /* ---- collapsed SHOP menu: flyout placement --------------------------- */

    /* The flyout is anchored to its row, so a row low down the list would push
       it off the bottom of the screen. Clamp it on open, and swap it to the left
       if there is no room on the right. */
    function placeFlyout(row) {
      var flyout = row.querySelector('.hcs-nav__flyout');
      if (!flyout) return;

      row.classList.remove('hcs-nav__shop-row--flip');
      flyout.style.top = '';
      flyout.style.maxHeight = '';

      var rowBox = row.getBoundingClientRect();
      var margin = 8;

      /* ⚑ GIVE THE FLYOUT THE WHOLE WINDOW BEFORE MEASURING IT.
         The stylesheet caps it at a rem figure that is smaller than most
         screens, so a 17-item menu lost its last two entries to a limit
         that had nothing to do with how much room there was. The cap is now
         the space actually on screen.
         ⚑ ORDER MATTERS: this must be set BEFORE offsetHeight is read
         below, or the lift is computed from the old clipped height and the
         menu is pulled up further than it needs to be.
         A short menu is unaffected - a flyout is as tall as its contents
         and this is only a ceiling. */
      flyout.style.maxHeight = Math.max(160, window.innerHeight - margin * 2) + 'px';

      /* horizontal */
      var width = flyout.offsetWidth || 280;
      if (rowBox.right + width > window.innerWidth - margin) {
        row.classList.add('hcs-nav__shop-row--flip');
      }

      /* vertical: default sits 0.8rem above the row's top */
      var height = flyout.offsetHeight;
      var defaultTop = rowBox.top - parseFloat(window.getComputedStyle(flyout).paddingTop || 0);
      var overshoot = defaultTop + height - (window.innerHeight - margin);
      if (overshoot > 0) {
        var lift = Math.min(overshoot, Math.max(0, defaultTop - margin));
        flyout.style.top = -lift + 'px';
      }
    }

    var shopRows = Array.prototype.slice.call(
      nav.querySelectorAll('.hcs-nav__shop-row--has-sub')
    );

    shopRows.forEach(function (row) {
      row.addEventListener('pointerenter', function () {
        placeFlyout(row);
      });
      row.addEventListener('focusin', function () {
        placeFlyout(row);
      });
    });

    /* ---- touch: tap opens instead of navigating -------------------------- */

    function closeAll(except) {
      var open = nav.querySelectorAll(
        '.hcs-nav__item.is-open, .hcs-nav__shop-row.is-open, .hcs-nav__sub.is-open'
      );
      for (var i = 0; i < open.length; i++) {
        if (open[i] === except) continue;
        /* keep an ancestor open: closing SHOP would close the flyout inside it */
        if (except && open[i].contains(except)) continue;
        open[i].classList.remove('is-open');
        var l = open[i].querySelector('.hcs-nav__link');
        if (l) l.setAttribute('aria-expanded', 'false');
      }
    }

    if (window.matchMedia && window.matchMedia('(hover: none)').matches) {
      nav.addEventListener('click', function (e) {
        /* the Brands row inside the Nutrients dropdown (6 Sep 2026): first tap
           opens the brands flyout, second tap follows the link */
        var subLink = e.target.closest && e.target.closest('.hcs-nav__sub-link');
        if (subLink) {
          var sub = subLink.parentElement;
          if (!sub || !sub.classList.contains('hcs-nav__sub')) return;
          if (sub.classList.contains('is-open')) return;
          e.preventDefault();
          closeAll(sub);
          sub.classList.add('is-open');
          return;
        }

        /* a row inside the collapsed SHOP menu that has a flyout */
        var shopLink = e.target.closest && e.target.closest('.hcs-nav__shop-link');
        if (shopLink) {
          var row = shopLink.parentElement;
          if (!row || !row.classList.contains('hcs-nav__shop-row--has-sub')) return;
          if (row.classList.contains('is-open')) return; /* second tap follows the link */
          e.preventDefault();
          closeAll(row);
          placeFlyout(row);
          row.classList.add('is-open');
          return;
        }

        var link = e.target.closest && e.target.closest('.hcs-nav__link');
        if (!link) return;
        var li = link.parentElement;
        if (!li || !li.classList.contains('hcs-nav__item--has-panel')) return;
        if (li.classList.contains('is-open')) return; /* second tap follows the link */
        e.preventDefault();
        closeAll(li);
        li.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
      });

      document.addEventListener('click', function (e) {
        if (!nav.contains(e.target)) closeAll(null);
      });
    }

    nav.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeAll(null);
      var focused = document.activeElement;
      var li = focused && focused.closest && focused.closest('.hcs-nav__item');
      var top = li && li.querySelector('.hcs-nav__link');
      if (top) top.focus();
    });

    /* ---- triggers ------------------------------------------------------- */

    window.addEventListener('resize', function () {
      schedule(false);
    });
    window.addEventListener('orientationchange', relayout);

    /* the sticky header puts account + basket back into this bar, which takes
       width off the nav without a window resize. Watched via the class that
       does it — a ResizeObserver here would see the height changes this code
       itself causes. */
    var section = nav.closest('.section-header');
    if (section && window.MutationObserver) {
      new MutationObserver(function () {
        schedule(false);
      }).observe(section, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('visibilitychange', relayout);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(relayout).catch(function () {});
    }

    nav.__hcsRelayout = relayout; /* for verification from the console */

    /* straight away, not deferred to a frame, so the bar is never briefly wrong */
    lastWidth = Math.round(nav.clientWidth);
    layout();
    window.setTimeout(relayout, 400);
  }

  /* ---- checkout shortcut in the collapsed bar ---------------------------- */

  /* Shown only with something in the cart. Dawn re-renders #cart-icon-bubble on
     every cart change but leaves its siblings alone, so the button's state is
     derived from that element rather than tracked separately. */
  function syncCheckoutButton() {
    try {
      var btn = document.querySelector('[data-hcs-checkout]');
      if (!btn) return;
      var bubble = document.getElementById('cart-icon-bubble');
      var hasItems = !!(bubble && bubble.querySelector('.cart-count-bubble'));
      btn.classList.toggle('is-empty', !hasItems);
    } catch (e) {}
  }

  function watchCart() {
    syncCheckoutButton();
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !window.MutationObserver) return;
    new MutationObserver(syncCheckoutButton).observe(bubble, {
      childList: true,
      subtree: true
    });
    /* the bubble element itself is replaced wholesale by some cart flows */
    var host = bubble.parentElement;
    if (host) {
      new MutationObserver(function () {
        syncCheckoutButton();
      }).observe(host, { childList: true });
    }
  }

  function boot() {
    var navs = document.querySelectorAll('.hcs-nav');
    for (var i = 0; i < navs.length; i++) init(navs[i]);
    watchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
